import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import { ENDPOINTS } from '../components/Sistema/Utils/Endpoints';
import { API_BASE } from '../api';
import { getSessionUserId } from '../utils/SessionUser';
import { ENDPOINTS as GENERAL_ENDPOINTS } from '../components/General/Utils/Endpoints';

const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const getFormattedUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${SERVER_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const LogoContext = createContext();

export const useLogos = () => useContext(LogoContext);

export const LogoProvider = ({ children }) => {
    // --- CACHE KEYS ---
    const CACHE_KEY_PREFIX = 'SIGMA_RESOURCES_';
    const getKey = (key) => `${CACHE_KEY_PREFIX}${key}`;

    // --- INITIALIZERS (Cache-First Strategy) ---
    const loadFromCache = (key, defaultValue) => {
        try {
            const cached = localStorage.getItem(getKey(key));
            return cached ? JSON.parse(cached) : defaultValue;
        } catch (e) {
            return defaultValue;
        }
    };

    // 1. Data Lists (Compatibilidad para Managers/Catalogos internos)
    const [logos, setLogos] = useState(() => loadFromCache('LOGOS_DATA', []));
    const [systemImages, setSystemImages] = useState(() => loadFromCache('IMAGES_DATA', []));

    // 2. ESTRATEGIA DE DOBLE CACHÉ (Public vs Tenant)

    // PÚBLICO: Se carga siempre (Login page, Defaults)
    const [publicLogoMap, setPublicLogoMap] = useState(() => loadFromCache('PUBLIC_LOGO_MAP', {}));
    const [publicImagesMap, setPublicImagesMap] = useState(() => loadFromCache('PUBLIC_IMAGES_MAP', {}));

    // PRIVADO/TENANT: Se carga solo con sesión activa y tiene prioridad
    const [tenantLogoMap, setTenantLogoMap] = useState(() => loadFromCache('TENANT_LOGO_MAP', {}));
    const [tenantImagesMap, setTenantImagesMap] = useState(() => loadFromCache('TENANT_IMAGES_MAP', {}));

    // Configs (Globales por ahora)
    const [configsMap, setConfigsMap] = useState(() => loadFromCache('CONFIGS_MAP', {}));

    // Loading inicial: solo si no existen los mapas públicos mínimos
    const [loading, setLoading] = useState(() => {
        return !localStorage.getItem(getKey('PUBLIC_LOGO_MAP'));
    });

    // --- REFRESH FUNCTION ---
    const refreshResources = async () => {
        setLoading(true);
        try {
            await fetchData();
        } catch (error) {
            console.error("Error refreshing resources:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        try {
            const idUsuario = getSessionUserId();
            let idEmpresa = null;

            // 1. Identificar Empresa (Si hay usuario)
            if (idUsuario) {
                try {
                    const resEmpresas = await api.get(GENERAL_ENDPOINTS.empresasUsuario(idUsuario));
                    const empresas = resEmpresas.data || [];
                    if (empresas.length > 0) {
                        idEmpresa = empresas[0].idEmpresa || empresas[0].IdEmpresa;
                        // console.log(`[LogoProvider] Usuario ${idUsuario} -> Empresa ${idEmpresa}.`);
                    }
                } catch (e) {
                    console.warn('[LogoProvider] Fallo al obtener empresa, usando defaults.', e);
                }
            }

            // 2. Limpieza de Seguridad: Si NO hay empresa (pero sí usuario o logout), 
            // debemos asegurar que el cache de la empresa anterior no se quede "pegado"
            if (!idEmpresa) {
                setTenantLogoMap({});
                setTenantImagesMap({});
                localStorage.removeItem(getKey('TENANT_LOGO_MAP'));
                localStorage.removeItem(getKey('TENANT_IMAGES_MAP'));
            }

            // 3. Preparar Peticiones
            // SIEMPRE pedimos los Defaults (Públicos) para asegurar que existan los recursos base
            const promises = [
                api.get(ENDPOINTS.logosDefault),            // index 0
                api.get(ENDPOINTS.imagenesSistemaDefault),  // index 1
                api.get(ENDPOINTS.configuracionesSistema)   // index 2
            ];

            // Si hay empresa, pedimos ADEMÁS los recursos del Tenant
            if (idEmpresa) {
                promises.push(api.get(`${ENDPOINTS.logos}?idEmpresa=${idEmpresa}`));           // index 3
                promises.push(api.get(`${ENDPOINTS.imagenesSistema}?idEmpresa=${idEmpresa}`));  // index 4
            }

            const results = await Promise.all(promises);

            // 4. Procesar DEFAULT/PUBLIC Data
            const publicLogosData = results[0]?.data || [];
            const publicImagesData = results[1]?.data || [];
            const configsData = results[2]?.data || [];

            const pLMap = buildMap(publicLogosData, 'logo');
            const pIMap = buildMap(publicImagesData, 'imagen');
            const cMap = buildConfigsMap(configsData);

            setPublicLogoMap(pLMap);
            setPublicImagesMap(pIMap);
            setConfigsMap(cMap);

            localStorage.setItem(getKey('PUBLIC_LOGO_MAP'), JSON.stringify(pLMap));
            localStorage.setItem(getKey('PUBLIC_IMAGES_MAP'), JSON.stringify(pIMap));
            localStorage.setItem(getKey('CONFIGS_MAP'), JSON.stringify(cMap));

            // Por defecto activo = público (se sobreescribirá si hay tenant)
            let finalLogosList = publicLogosData;
            let finalImagesList = publicImagesData;

            // 5. Procesar TENANT Data (si aplica)
            if (idEmpresa) {
                const tenantLogosData = results[3]?.data || [];
                const tenantImagesData = results[4]?.data || [];

                finalLogosList = tenantLogosData;
                finalImagesList = tenantImagesData;

                const tLMap = buildMap(tenantLogosData, 'logo');
                const tIMap = buildMap(tenantImagesData, 'imagen');

                setTenantLogoMap(tLMap);
                setTenantImagesMap(tIMap);

                localStorage.setItem(getKey('TENANT_LOGO_MAP'), JSON.stringify(tLMap));
                localStorage.setItem(getKey('TENANT_IMAGES_MAP'), JSON.stringify(tIMap));
            }

            // 6. Actualizar listas planas (Para Managers, compatibilidad)
            setLogos(finalLogosList);
            setSystemImages(finalImagesList);
            localStorage.setItem(getKey('LOGOS_DATA'), JSON.stringify(finalLogosList));
            localStorage.setItem(getKey('IMAGES_DATA'), JSON.stringify(finalImagesList));

            // console.log(`🎨 Recursos actualizados. Tenant: ${idEmpresa ? 'ACTIVO' : 'INACTIVO'}`);

            // 7. Favicon (Combinando mapas: Tenant gana a Public)
            const combinedMap = { ...pLMap, ...((idEmpresa && Object.keys(tenantLogoMap).length > 0) ? tenantLogoMap : {}) };

            // Si acabamos de traer datos tenant, los mezclamos manualmente para asegurar frescura inmediata
            if (idEmpresa) {
                const tempTenantMap = buildMap(finalLogosList, 'logo');
                Object.assign(combinedMap, tempTenantMap);
            }
            updateFaviconFromMap(combinedMap);

        } catch (error) {
            console.error("❌ Error cargando recursos:", error);
        }
    };

    // --- HELPERS ---
    const buildMap = (list, type) => {
        const map = {};
        list.forEach(item => {
            const name = type === 'logo'
                ? (item.nombreTipoLogo || item.NombreTipoLogo)
                : (item.nombreTipoImagen || item.NombreTipoImagen);

            const url = item.imagenUrl || item.ImagenUrl;
            if (name && url) {
                map[name] = getFormattedUrl(url);
            }
        });
        return map;
    };

    const buildConfigsMap = (list) => {
        const map = {};
        list.forEach(item => {
            const name = item.nombre || item.Nombre;
            const value = item.valor || item.Valor;
            if (name) map[name] = value;
        });
        return map;
    };

    const updateFaviconFromMap = (map) => {
        const faviconKey = Object.keys(map).find(k => k.toLowerCase() === 'favicon');
        if (faviconKey && map[faviconKey]) {
            updateFavicon(map[faviconKey]);
        }
    };

    const updateFavicon = (url) => {
        const links = document.querySelectorAll("link[rel*='icon']");
        links.forEach(link => link.parentNode.removeChild(link));

        const isPng = url.toLowerCase().endsWith('.png');
        const mimeType = isPng ? 'image/png' : 'image/x-icon';
        const rels = ['icon', 'shortcut icon'];

        rels.forEach(rel => {
            const link = document.createElement('link');
            link.rel = rel;
            link.type = mimeType;
            const separator = url.includes('?') ? '&' : '?';
            link.href = `${url}${separator}t=${new Date().getTime()}`;
            document.getElementsByTagName('head')[0].appendChild(link);
        });
    };

    // --- GETTERS (MERGE LOGIC) ---
    // Clave: Busca primero en Tenant (personalizado), si no, usa Public (default)

    const getLogoUrl = (tipo, fallbackUrl = '') => {
        const url = tenantLogoMap[tipo] || publicLogoMap[tipo];
        return url || fallbackUrl;
    };

    const getSystemImageUrl = (tipo, fallbackUrl = '') => {
        const url = tenantImagesMap[tipo] || publicImagesMap[tipo];
        return url || fallbackUrl;
    };

    const getSystemConfig = (nombre, fallback = null) => {
        return configsMap[nombre] !== undefined ? configsMap[nombre] : fallback;
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- STRICT PUBLIC GETTERS ---
    // Para componentes que SIEMPRE deben mostrar branding default (Login, Public Layouts)
    const getPublicLogoUrl = (tipo, fallbackUrl = '') => {
        return publicLogoMap[tipo] || fallbackUrl;
    };

    return (
        <LogoContext.Provider value={{
            logos, // Legacy support
            systemImages, // Legacy support
            getLogoUrl, // Auto-merge (Tenant > Public)
            getPublicLogoUrl, // Strict Public
            getSystemImageUrl,
            getSystemConfig,
            loading,
            refreshResources
        }}>
            {children}
        </LogoContext.Provider>
    );
};
